import styles from "./object-passport.module.css";

import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { App, Spin, Button, Typography } from "antd";

import { ArrowUpOutlined } from "@ant-design/icons";

import ObjectFieldsTable from "@/components/tables/ObjectFieldsTable";
import RelatedObjectsTable from "@/components/tables/RelatedObjectsTable";
import EmployeeFieldsTable from "@/components/tables/EmployeeFieldsTable";

import Header from "./components/Header";
import Footer from "./components/Footer";
import Aside from "./components/Aside";
import FileView from "./components/FileView";
import PDFButton from "./components/PDFButton";

import { getPassportData } from "@/api/objects";
import { getFullName, removeEmoji } from "@/utils";

const SCROLL_TOP_BUTTON_OFFSET = 120;

const ObjectPassport = ({ config }) => {
  const { id } = useParams();
  const { message } = App.useApp();

  // Состояния для хранения данных
  // Данные паспорта
  const [passportData, setPassportData] = useState(null);
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);

  const [isLoading, setIsLoading] = useState(true); // Загрузка данных

  const [mapScreenshots, setMapScreenshots] = useState({}); // {key, url}
  const handlePolygonScreenshot = ({ field_uuid, url, object_uuid }) => {
    // Составной ключ из object_uuid и field_uuid
    const key = `${object_uuid}_${field_uuid}`;
    setMapScreenshots((prev) => ({
      ...prev,
      [key]: url,
    }));
  };

  // Первоначальный запрос данных при загрузке компонента
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      const shouldShow = window.scrollY > SCROLL_TOP_BUTTON_OFFSET;
      setShowScrollTopButton((prev) =>
        prev === shouldShow ? prev : shouldShow,
      );
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await getPassportData(id);

      const groupsFields =
        res.definition?.groups
          .filter(({ fields }) => !!fields && !!fields.length)
          .sort((a, b) => a.idx - b.idx) || [];

      const relatedEmployees =
        groupsFields
          .flatMap(({ fields }) =>
            fields
              .filter(({ type }) => type === "employee")
              .map(({ field_name, name, uuid }) => {
                const value = res.object[field_name];

                if (!value) return null;
                return { ...value, field_name: name, field_uuid: uuid };
              }),
          )
          .filter((employee) => !!employee) || [];

      setPassportData({
        data: res,
        object: res.object,
        groupsFields,
        subObjects: res.relatedObjects?.data || [],
        relatedObjects: res.referencingObjects?.data || [],
        files: res.relatedFiles?.data || [],
        color: res.definition?.color,
        relatedEmployees,
      });
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Скролл до нужного раздела страницы
  const scrollTo = (elementId) => {
    const targetElement = document.getElementById(elementId);
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const navs = useMemo(() => {
    if (!passportData) return [];

    const { object, subObjects, relatedObjects, files, relatedEmployees } =
      passportData;

    const baseNavs = [{ uuid: object.uuid, name: object.name }];
    const subObjectNavs =
      subObjects?.map((subObject) => ({
        uuid: subObject.object.uuid,
        name: subObject.object.name,
      })) || [];
    const relatedObjectNav = !!relatedObjects?.length
      ? [{ uuid: "related-objects", name: "Связанные объекты" }]
      : [];
    const employeesNavs =
      relatedEmployees?.map((employee) => ({
        uuid: employee.field_uuid,
        name: employee.field_name,
      })) || [];

    const fileNavs = files.map((file) => ({
      uuid: file.uuid,
      name: file.original_name,
    }));

    return [
      ...baseNavs,
      ...subObjectNavs,
      ...relatedObjectNav,
      ...employeesNavs,
      ...fileNavs,
    ];
  }, [passportData]);

  return (
    <div className={styles.page}>
      {isLoading ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        passportData && (
          <>
            <Header
              config={config}
              date={passportData.data.created_at}
              printBtn={
                <PDFButton
                  data={passportData}
                  navs={navs}
                  screenshots={mapScreenshots}
                  config={config}
                  color="primary"
                  variant="solid"
                  iconColor="#fff"
                  textColor="fff"
                />
              }
            />

            <div className={styles.body} id="top">
              <div className={styles.container}>
                <div className={styles.content}>
                  <section
                    className={styles.section}
                    id={passportData.object.uuid}
                  >
                    <Typography.Title level={2} className={styles.title}>
                      {passportData.object.name}
                    </Typography.Title>

                    {/* Разделы объекта */}
                    {passportData.groupsFields.map((group) => {
                      return (
                        <div key={group.uuid} id={group.uuid}>
                          <Typography.Title
                            level={4}
                            style={{ margin: "24px 0 5px" }}
                          >
                            {group.name}
                          </Typography.Title>

                          <ObjectFieldsTable
                            theme="passport"
                            fields={group.fields || []}
                            units={[]} //нужно передать пустой массив иначе начинается бесконечный рендеринг
                            object={passportData.object}
                            subObjects={passportData.subObjects}
                            onScreenshot={handlePolygonScreenshot}
                            color={passportData.color}
                          />
                        </div>
                      );
                    })}
                  </section>

                  {/* Подобъекты */}
                  {passportData.subObjects.map((subObject) => {
                    return (
                      <section
                        className={styles.section}
                        key={subObject.object.uuid}
                        id={subObject.object.uuid}
                      >
                        <Typography.Title level={2} className={styles.title}>
                          {subObject.object.name}
                        </Typography.Title>

                        {/* Разделы подобъекта */}
                        {subObject.definition.groups
                          .filter(({ fields }) => !!fields && !!fields.length)
                          .sort((a, b) => a.idx - b.idx)
                          .map((group) => {
                            return (
                              <div key={group.uuid} id={group.uuid}>
                                <Typography.Title
                                  level={4}
                                  style={{ margin: "24px 0 5px" }}
                                >
                                  {group.name}
                                </Typography.Title>

                                <ObjectFieldsTable
                                  theme="passport"
                                  variant="not-link"
                                  fields={group.fields || []}
                                  units={[]} //нужно передать пустой массив иначе начинается бесконечный рендеринг
                                  object={subObject.object}
                                  onScreenshot={handlePolygonScreenshot}
                                  color={subObject.definition.color}
                                />
                              </div>
                            );
                          })}
                      </section>
                    );
                  })}

                  {/* Связанные объекты */}
                  {!!passportData.relatedObjects?.length && (
                    <section className={styles.section} id="related-objects">
                      <Typography.Title level={2} className={styles.title}>
                        Связанные объекты
                      </Typography.Title>

                      <RelatedObjectsTable
                        data={passportData.relatedObjects}
                        theme="passport"
                      />
                    </section>
                  )}

                  {/* Сотрудники */}
                  {passportData.relatedEmployees.map(
                    ({ field_uuid, field_name, ...value }) => {
                      return (
                        <section
                          className={styles.section}
                          key={field_uuid}
                          id={field_uuid}
                        >
                          <Typography.Title level={2} className={styles.title}>
                            {field_name}
                          </Typography.Title>

                          <EmployeeFieldsTable
                            data={{
                              ...value,
                              name: getFullName(
                                value.lastname,
                                value.firstname,
                                value.patronymic,
                              ),
                            }}
                          />
                        </section>
                      );
                    },
                  )}

                  {/* Файлы */}
                  {passportData.files.map((file) => {
                    return (
                      <section
                        className={styles.section}
                        key={file.uuid}
                        id={file.uuid}
                      >
                        <Typography.Title level={2} className={styles.title}>
                          {file.original_name}
                        </Typography.Title>

                        <FileView file={file} />
                      </section>
                    );
                  })}

                  {showScrollTopButton && (
                    <Button
                      icon={<ArrowUpOutlined />}
                      className={styles.scrollTopButton}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollTo("top");
                      }}
                    >
                      Наверх
                    </Button>
                  )}
                </div>

                <Aside
                  objectName={passportData.object.name}
                  objectType={removeEmoji(passportData.data.definition?.name)}
                  createDate={passportData.data.created_at}
                  author={passportData.data.author}
                  navs={navs}
                  navClick={scrollTo}
                />
              </div>
            </div>

            <Footer
              config={config}
              date={passportData.data.created_at}
              author={passportData.data.author}
              printBtn={
                <PDFButton
                  data={passportData}
                  navs={navs}
                  screenshots={mapScreenshots}
                  config={config}
                  color="default"
                  variant="outlined"
                  iconColor="var(--color-gray-m)"
                  textColor="var(--color-gray-d)"
                />
              }
            />
          </>
        )
      )}
    </div>
  );
};

export default ObjectPassport;
